import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

const STATUS_OPTIONS = ['pending','in_progress','complete','on_hold'];

const inputStyle = {
  width:'100%', padding:'8px 10px', border:'1.5px solid var(--gray5)',
  borderRadius:8, fontSize:14, fontFamily:'inherit', outline:'none',
};

export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({ job_number:'', title:'', description:'', due_date:'' });
  const [showAddAsm, setShowAddAsm] = useState(false);
  const [asmForm, setAsmForm] = useState({ name:'', part_number:'', quantity:1 });
  const [showAddOp, setShowAddOp] = useState({});
  const [opForm, setOpForm] = useState({});

  // Edit job header state
  const [editJob, setEditJob] = useState(false);
  const [editJobForm, setEditJobForm] = useState({});

  // Op history (time logs + photos)
  const [opLogs, setOpLogs] = useState({});
  const [opLogsLoading, setOpLogsLoading] = useState({});

  const toggleOpLogs = async (opId) => {
    if (opLogs[opId]) { setOpLogs(s => ({ ...s, [opId]: null })); return; }
    setOpLogsLoading(s => ({ ...s, [opId]: true }));
    try {
      const { data } = await api.get(`/time-logs/operation/${opId}`);
      setOpLogs(s => ({ ...s, [opId]: data }));
    } catch { /* ignore */ }
    finally { setOpLogsLoading(s => ({ ...s, [opId]: false })); }
  };

  // Edit state
  const [editAsmId, setEditAsmId] = useState(null);
  const [editAsmForm, setEditAsmForm] = useState({});
  const [editOpId, setEditOpId] = useState(null);
  const [editOpForm, setEditOpForm] = useState({});

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const { data } = await api.get(`/jobs/${id}`);
      setJob(data);
    } catch { setError('Job not found'); }
    finally { setLoading(false); }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  const createJob = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const { data } = await api.post('/jobs', form);
      navigate(`/jobs/${data.id}`);
    } catch (err) { setError(err.response?.data?.error || 'Failed to create job'); }
    finally { setSaving(false); }
  };

  const updateJobStatus = async (status) => {
    try {
      const { data } = await api.patch(`/jobs/${id}`, { status });
      setJob(j => ({ ...j, status: data.status }));
    } catch { setError('Failed to update status'); }
  };

  const saveJobDetails = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const { data } = await api.patch(`/jobs/${id}`, {
        job_number: editJobForm.job_number,
        title: editJobForm.title,
        description: editJobForm.description || null,
        due_date: editJobForm.due_date || null,
      });
      setJob(j => ({ ...j, job_number: data.job_number, title: data.title, description: data.description, due_date: data.due_date }));
      setEditJob(false);
    } catch (err) { setError(err.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const addAssembly = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post('/assemblies', { ...asmForm, job_id: id, quantity: parseInt(asmForm.quantity) });
      setShowAddAsm(false); setAsmForm({ name:'', part_number:'', quantity:1 }); load();
    } catch (err) { setError(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const saveAssembly = async (e, asmId) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.patch(`/assemblies/${asmId}`, {
        name: editAsmForm.name,
        part_number: editAsmForm.part_number || null,
        quantity: parseInt(editAsmForm.quantity) || 1,
      });
      setEditAsmId(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const deleteAssembly = async (asm) => {
    if (!window.confirm(`Delete assembly "${asm.name}"?\n\nThis will also delete all its operations and time logs.`)) return;
    try {
      await api.delete(`/assemblies/${asm.id}`); load();
    } catch { setError('Failed to delete assembly'); }
  };

  // Auto-suggest next op code for an assembly (010, 020, 030...)
  const nextOpCode = (assemblyId) => {
    const asm = (job?.assemblies || []).find(a => a.id === assemblyId);
    const ops = asm?.operations || [];
    const codes = ops
      .map(o => parseInt(o.op_code))
      .filter(n => !isNaN(n));
    if (codes.length === 0) return '010';
    const max = Math.max(...codes);
    return String(max + 10).padStart(3, '0');
  };

  const addOperation = async (e, assemblyId) => {
    e.preventDefault(); const f = opForm[assemblyId] || {}; setSaving(true);
    try {
      await api.post('/operations', {
        assembly_id: assemblyId,
        op_code: f.op_code || null,
        name: f.name,
        description: f.description,
        estimated_minutes: f.estimated_minutes ? parseInt(f.estimated_minutes) : null,
      });
      setShowAddOp(s => ({ ...s, [assemblyId]: false }));
      setOpForm(s => ({ ...s, [assemblyId]: {} })); load();
    } catch (err) { setError(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const saveOperation = async (e, opId) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.patch(`/operations/${opId}`, {
        op_code: editOpForm.op_code || null,
        name: editOpForm.name,
        description: editOpForm.description || null,
        estimated_minutes: editOpForm.estimated_minutes ? parseInt(editOpForm.estimated_minutes) : null,
      });
      setEditOpId(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const deleteOperation = async (op) => {
    if (!window.confirm(`Delete operation "${op.name}"?\n\nThis will also delete its time logs.`)) return;
    try {
      await api.delete(`/operations/${op.id}`); load();
    } catch { setError('Failed to delete operation'); }
  };

  const updateOpStatus = async (opId, status) => {
    try { await api.patch(`/operations/${opId}`, { status }); load(); }
    catch { setError('Failed to update'); }
  };

  const duplicateJob = async () => {
    const match = job.job_number.match(/^(.*?)(\d+)$/);
    let suggested;
    if (match) {
      suggested = match[1] + String(parseInt(match[2]) + 1).padStart(match[2].length, '0');
    } else {
      suggested = job.job_number + '-2';
    }
    const newJobNumber = window.prompt('Job number for the duplicate:', suggested);
    if (newJobNumber === null) return;
    if (!newJobNumber.trim()) { setError('Job number is required'); return; }
    try {
      const { data } = await api.post(`/jobs/${id}/duplicate`, { job_number: newJobNumber.trim() });
      navigate(`/jobs/${data.id}`);
    } catch (err) { setError(err.response?.data?.error || 'Failed to duplicate job'); }
  };

  const deleteJob = async () => {
    if (!window.confirm(`Delete job "${job.job_number} — ${job.title}"?\n\nThe job will be moved to the deleted jobs log and can be restored later.`)) return;
    try { await api.delete(`/jobs/${id}`); navigate('/dashboard'); }
    catch { setError('Failed to delete job'); }
  };

  // ── New job form ──
  if (isNew) {
    return (
      <div className="page">
        <div className="topbar">
          <button className="btn btn-ghost" style={{ padding:'6px 12px', fontSize:13 }} onClick={() => navigate('/dashboard')}>← Back</button>
          <h1>New Job</h1>
          <div />
        </div>
        <div className="content" style={{ maxWidth:560 }}>
          <div className="card">
            {error && <div className="error-msg">{error}</div>}
            <form onSubmit={createJob}>
              <div className="field"><label>Job number</label>
                <input value={form.job_number} onChange={e=>setForm(f=>({...f,job_number:e.target.value}))} placeholder="e.g. JOB-2024-001" required />
              </div>
              <div className="field"><label>Title</label>
                <input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="Job description" required />
              </div>
              <div className="field"><label>Description (optional)</label>
                <textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}
                  rows={3} style={{ width:'100%', padding:'10px 12px', border:'1.5px solid var(--gray5)', borderRadius:8, fontSize:15, fontFamily:'inherit', resize:'vertical' }} />
              </div>
              <div className="field"><label>Due date (optional)</label>
                <input type="date" value={form.due_date} onChange={e=>setForm(f=>({...f,due_date:e.target.value}))} />
              </div>
              <button className="btn btn-primary btn-lg" type="submit" disabled={saving}>
                {saving ? 'Creating…' : 'Create job'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div className="loading-screen">Loading job…</div>;
  if (!job) return <div className="loading-screen">{error || 'Job not found'}</div>;

  return (
    <div className="page">
      <div className="topbar">
        <button className="btn btn-ghost" style={{ padding:'6px 12px', fontSize:13 }} onClick={() => navigate('/dashboard')}>
          ← Dashboard
        </button>
        <h1 style={{ fontSize:15 }}>{job.job_number}</h1>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <span className={`badge badge-${job.status}`}>{job.status.replace('_',' ')}</span>
          <button className="btn btn-ghost" style={{ padding:'6px 12px', fontSize:13 }} onClick={() => navigate(`/jobs/${id}/qr-sheet`)}>
            QR Sheet
          </button>
        </div>
      </div>

      <div className="content">
        {error && <div className="error-msg" style={{ marginBottom:12 }}>{error}</div>}

        {/* Job header */}
        <div className="card" style={{ marginBottom:14 }}>
          {editJob ? (
            <form onSubmit={saveJobDetails}>
              <div className="field">
                <label>Job number</label>
                <input style={inputStyle} value={editJobForm.job_number} onChange={e=>setEditJobForm(f=>({...f,job_number:e.target.value}))} required />
              </div>
              <div className="field">
                <label>Title</label>
                <input style={inputStyle} value={editJobForm.title} onChange={e=>setEditJobForm(f=>({...f,title:e.target.value}))} required />
              </div>
              <div className="field">
                <label>Description</label>
                <textarea style={{ ...inputStyle, resize:'vertical' }} rows={3}
                  value={editJobForm.description||''} onChange={e=>setEditJobForm(f=>({...f,description:e.target.value}))} />
              </div>
              <div className="field">
                <label>Due date</label>
                <input type="date" style={inputStyle} value={editJobForm.due_date||''} onChange={e=>setEditJobForm(f=>({...f,due_date:e.target.value}))} />
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button className="btn btn-success" type="submit" style={{ padding:'6px 14px', fontSize:13 }} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button className="btn btn-ghost" type="button" style={{ padding:'6px 14px', fontSize:13 }} onClick={() => setEditJob(false)}>
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
                <div style={{ fontWeight:700, fontSize:18 }}>{job.title}</div>
                <button className="btn btn-ghost" style={{ padding:'4px 12px', fontSize:13, flexShrink:0, marginLeft:10 }}
                  onClick={() => { setEditJob(true); setEditJobForm({ job_number:job.job_number, title:job.title, description:job.description||'', due_date: job.due_date ? job.due_date.slice(0,10) : '' }); }}>
                  Edit
                </button>
              </div>
              {job.description && <div style={{ color:'var(--gray3)', fontSize:14, marginBottom:10 }}>{job.description}</div>}
              {job.due_date && <div style={{ fontSize:13, color:'var(--gray3)', marginBottom:10 }}>Due: {new Date(job.due_date).toLocaleDateString()}</div>}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', flexWrap:'wrap', gap:8 }}>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {STATUS_OPTIONS.map(s => (
                    <button key={s} className={`btn ${job.status === s ? 'btn-primary' : 'btn-ghost'}`}
                      style={{ padding:'6px 14px', fontSize:13 }} onClick={() => updateJobStatus(s)}>
                      {s.replace('_',' ')}
                    </button>
                  ))}
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-ghost" style={{ padding:'6px 14px', fontSize:13 }} onClick={duplicateJob}>
                    Duplicate job
                  </button>
                  <button className="btn btn-danger" style={{ padding:'6px 14px', fontSize:13 }} onClick={deleteJob}>
                    Delete job
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Assemblies header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
          <div className="section-title" style={{ marginBottom:0 }}>Assemblies</div>
          <button className="btn btn-primary" style={{ padding:'6px 14px', fontSize:13 }} onClick={() => setShowAddAsm(true)}>
            + Assembly
          </button>
        </div>

        {/* Add assembly form */}
        {showAddAsm && (
          <div className="card" style={{ marginBottom:12, borderLeft:'3px solid var(--blue)' }}>
            <div style={{ fontWeight:600, marginBottom:10 }}>Add assembly</div>
            <form onSubmit={addAssembly}>
              <div className="field"><label>Name</label>
                <input value={asmForm.name} onChange={e=>setAsmForm(f=>({...f,name:e.target.value}))} placeholder="Assembly name" required />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="field"><label>Part number</label>
                  <input value={asmForm.part_number} onChange={e=>setAsmForm(f=>({...f,part_number:e.target.value}))} placeholder="Optional" />
                </div>
                <div className="field"><label>Qty</label>
                  <input type="number" min="1" value={asmForm.quantity} onChange={e=>setAsmForm(f=>({...f,quantity:e.target.value}))} />
                </div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button className="btn btn-success" type="submit" disabled={saving}>Save</button>
                <button className="btn btn-ghost" type="button" onClick={() => setShowAddAsm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {(job.assemblies || []).length === 0 && (
          <div className="card" style={{ color:'var(--gray3)', textAlign:'center', padding:24 }}>
            No assemblies yet. Add one to start tracking operations.
          </div>
        )}

        {(job.assemblies || []).map(asm => (
          <div key={asm.id} className="card" style={{ marginBottom:12 }}>

            {/* Assembly header — edit mode */}
            {editAsmId === asm.id ? (
              <form onSubmit={e => saveAssembly(e, asm.id)} style={{ marginBottom:10 }}>
                <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 60px', gap:8, marginBottom:8 }}>
                  <div className="field" style={{ marginBottom:0 }}>
                    <label style={{ fontSize:12 }}>Name</label>
                    <input style={inputStyle} value={editAsmForm.name} onChange={e=>setEditAsmForm(f=>({...f,name:e.target.value}))} required />
                  </div>
                  <div className="field" style={{ marginBottom:0 }}>
                    <label style={{ fontSize:12 }}>Part number</label>
                    <input style={inputStyle} value={editAsmForm.part_number||''} onChange={e=>setEditAsmForm(f=>({...f,part_number:e.target.value}))} placeholder="Optional" />
                  </div>
                  <div className="field" style={{ marginBottom:0 }}>
                    <label style={{ fontSize:12 }}>Qty</label>
                    <input style={inputStyle} type="number" min="1" value={editAsmForm.quantity} onChange={e=>setEditAsmForm(f=>({...f,quantity:e.target.value}))} />
                  </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-success" type="submit" style={{ padding:'5px 14px', fontSize:13 }} disabled={saving}>Save</button>
                  <button className="btn btn-ghost" type="button" style={{ padding:'5px 14px', fontSize:13 }} onClick={() => setEditAsmId(null)}>Cancel</button>
                </div>
              </form>
            ) : (
              /* Assembly header — view mode */
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <div>
                  <span style={{ fontWeight:700, fontSize:15 }}>{asm.name}</span>
                  {asm.part_number && <span style={{ fontSize:12, color:'var(--gray3)', marginLeft:8 }}>{asm.part_number}</span>}
                  {asm.quantity > 1 && <span style={{ fontSize:12, color:'var(--gray3)', marginLeft:6 }}>×{asm.quantity}</span>}
                </div>
                <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                  <span className={`badge badge-${asm.status}`}>{asm.status.replace('_',' ')}</span>
                  <button className="btn btn-ghost" style={{ padding:'3px 10px', fontSize:12 }}
                    onClick={() => { setEditAsmId(asm.id); setEditAsmForm({ name:asm.name, part_number:asm.part_number||'', quantity:asm.quantity }); }}>
                    Edit
                  </button>
                  <button className="btn btn-danger" style={{ padding:'3px 10px', fontSize:12 }} onClick={() => deleteAssembly(asm)}>
                    Delete
                  </button>
                </div>
              </div>
            )}

            {/* Operations */}
            {(asm.operations || []).map(op => (
              <div key={op.id}>
                {editOpId === op.id ? (
                  /* Operation — edit mode */
                  <form onSubmit={e => saveOperation(e, op.id)}
                    style={{ padding:'10px', background:'var(--blue-lt)', borderRadius:8, marginBottom:6 }}>
                    <div style={{ display:'grid', gridTemplateColumns:'80px 1fr', gap:8 }}>
                      <div className="field" style={{ marginBottom:8 }}>
                        <label style={{ fontSize:12 }}>Op code</label>
                        <input style={inputStyle} value={editOpForm.op_code||''} onChange={e=>setEditOpForm(f=>({...f,op_code:e.target.value}))} placeholder="010" />
                      </div>
                      <div className="field" style={{ marginBottom:8 }}>
                        <label style={{ fontSize:12 }}>Operation name</label>
                        <input style={inputStyle} value={editOpForm.name} onChange={e=>setEditOpForm(f=>({...f,name:e.target.value}))} required />
                      </div>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:8 }}>
                      <div className="field" style={{ marginBottom:8 }}>
                        <label style={{ fontSize:12 }}>Notes / instructions</label>
                        <input style={inputStyle} value={editOpForm.description||''} onChange={e=>setEditOpForm(f=>({...f,description:e.target.value}))} placeholder="Optional" />
                      </div>
                      <div className="field" style={{ marginBottom:8 }}>
                        <label style={{ fontSize:12 }}>Est. minutes</label>
                        <input style={inputStyle} type="number" min="1" value={editOpForm.estimated_minutes||''} onChange={e=>setEditOpForm(f=>({...f,estimated_minutes:e.target.value}))} placeholder="e.g. 30" />
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      <button className="btn btn-success" type="submit" style={{ padding:'5px 14px', fontSize:13 }} disabled={saving}>Save</button>
                      <button className="btn btn-ghost" type="button" style={{ padding:'5px 14px', fontSize:13 }} onClick={() => setEditOpId(null)}>Cancel</button>
                    </div>
                  </form>
                ) : (
                  /* Operation — view mode */
                  <>
                  <div style={{
                    display:'flex', alignItems:'center', gap:8,
                    padding:'8px 10px', background:'var(--gray6)',
                    borderRadius:8, marginBottom:6,
                  }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:500, fontSize:14 }}>
                        {op.op_code && <span style={{ fontFamily:'monospace', fontSize:12, color:'var(--blue)', fontWeight:700, marginRight:6 }}>{op.op_code}</span>}
                        {op.name}
                      </div>
                      {op.description && <div style={{ fontSize:12, color:'var(--gray3)' }}>{op.description}</div>}
                      {op.estimated_minutes && <div style={{ fontSize:12, color:'var(--gray4)' }}>Est. {op.estimated_minutes}m</div>}
                    </div>
                    <select value={op.status} onChange={e => updateOpStatus(op.id, e.target.value)}
                      style={{ fontSize:13, padding:'4px 6px', borderRadius:6, border:'1px solid var(--gray5)', background:'#fff', cursor:'pointer' }}>
                      {['pending','in_progress','complete'].map(s => (
                        <option key={s} value={s}>{s.replace('_',' ')}</option>
                      ))}
                    </select>
                    <button className="btn btn-ghost" style={{ padding:'3px 10px', fontSize:12, flexShrink:0 }}
                      onClick={() => toggleOpLogs(op.id)}>
                      {opLogsLoading[op.id] ? '…' : opLogs[op.id] ? 'Hide history' : 'History'}
                    </button>
                    <button className="btn btn-ghost" style={{ padding:'3px 10px', fontSize:12, flexShrink:0 }}
                      onClick={() => { setEditOpId(op.id); setEditOpForm({ op_code:op.op_code||'', name:op.name, description:op.description||'', estimated_minutes:op.estimated_minutes||'' }); }}>
                      Edit
                    </button>
                    <button className="btn btn-danger" style={{ padding:'3px 10px', fontSize:12, flexShrink:0 }} onClick={() => deleteOperation(op)}>
                      Delete
                    </button>
                  </div>

                  {/* Time log history */}
                  {opLogs[op.id] && (
                    <div style={{ marginTop:8, padding:'10px 12px', background:'#fff', border:'1px solid var(--gray5)', borderRadius:8 }}>
                      {opLogs[op.id].length === 0 ? (
                        <div style={{ fontSize:13, color:'var(--gray3)' }}>No time logged yet.</div>
                      ) : opLogs[op.id].map(log => (
                        <div key={log.id} style={{ display:'flex', gap:12, alignItems:'flex-start', paddingBottom:10, marginBottom:10, borderBottom:'1px solid var(--gray6)' }}>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, fontWeight:600 }}>{log.worker_name}</div>
                            <div style={{ fontSize:12, color:'var(--gray3)' }}>
                              {new Date(log.started_at).toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric' })}
                              {' · '}{log.minutes} min
                            </div>
                            {log.notes && <div style={{ fontSize:12, color:'var(--gray2)', marginTop:3 }}>{log.notes}</div>}
                          </div>
                          {log.photo_url && (
                            <a href={log.photo_url} target="_blank" rel="noreferrer">
                              <img src={log.photo_url} alt="completion photo"
                                style={{ width:72, height:72, objectFit:'cover', borderRadius:6, border:'1px solid var(--gray5)', flexShrink:0 }} />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  </>
                )}
              </div>
            ))}

            {/* Add operation form */}
            {showAddOp[asm.id] ? (
              <form onSubmit={e => addOperation(e, asm.id)} style={{ marginTop:8, padding:'10px', background:'var(--blue-lt)', borderRadius:8 }}>
                <div style={{ display:'grid', gridTemplateColumns:'80px 1fr', gap:8 }}>
                  <div className="field"><label>Op code</label>
                    <input value={opForm[asm.id]?.op_code||''} onChange={e=>setOpForm(s=>({...s,[asm.id]:{...s[asm.id],op_code:e.target.value}}))} />
                  </div>
                  <div className="field"><label>Operation name</label>
                    <input value={opForm[asm.id]?.name||''} onChange={e=>setOpForm(s=>({...s,[asm.id]:{...s[asm.id],name:e.target.value}}))} placeholder="e.g. Drill holes" required />
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:8 }}>
                  <div className="field"><label>Notes / instructions</label>
                    <input value={opForm[asm.id]?.description||''} onChange={e=>setOpForm(s=>({...s,[asm.id]:{...s[asm.id],description:e.target.value}}))} placeholder="Optional" />
                  </div>
                  <div className="field"><label>Est. minutes</label>
                    <input type="number" min="1" value={opForm[asm.id]?.estimated_minutes||''} onChange={e=>setOpForm(s=>({...s,[asm.id]:{...s[asm.id],estimated_minutes:e.target.value}}))} placeholder="e.g. 30" />
                  </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-success" type="submit" disabled={saving}>Add</button>
                  <button className="btn btn-ghost" type="button" onClick={()=>setShowAddOp(s=>({...s,[asm.id]:false}))}>Cancel</button>
                </div>
              </form>
            ) : (
              <button className="btn btn-ghost" style={{ marginTop:6, fontSize:13, padding:'6px 12px' }}
                onClick={() => {
                  setOpForm(s => ({ ...s, [asm.id]: { ...s[asm.id], op_code: nextOpCode(asm.id) } }));
                  setShowAddOp(s => ({ ...s, [asm.id]: true }));
                }}>
                + Add operation
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
